import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { ChatMode, GespraechsPhase } from "@nl/shared";
import { getSocket, socketReady } from "@/lib/socket/connection";
import {
  PcmPlayer,
  concatSamples,
  floatToPcm16Base64,
  startRecorder,
  type Recorder,
} from "./pcm";

export type VoiceTurn = {
  role: "user" | "assistant";
  text: string;
};

export type VoiceStatus = "idle" | "connecting" | "live" | "ending" | "error";

/**
 * Which step of the connection we are on.
 *
 * Load-bearing for support, not cosmetics: every one of these steps can hang
 * silently, so different causes used to look identical to the advisor — an
 * endless "Verbinde…". Each one has a timeout and a message that names something
 * actionable. Background: docs/vorfall-feedback-und-live-ausfall.md.
 */
export type VoiceStage = "idle" | "mic" | "socket" | "session" | "live";

// Generous enough for a slow network, short enough that an advisor gets an
// answer instead of a spinner.
const MIC_TIMEOUT_MS = 20_000;
const SOCKET_TIMEOUT_MS = 8_000;
const SESSION_TIMEOUT_MS = 20_000;

/**
 * How many 100 ms blocks travel in one frame.
 *
 * Three, because in the customer's network the connection falls back to long
 * polling, where every frame is its own HTTP request — ten per second would make
 * the transport the bottleneck. The added latency is one block.
 */
const AUDIO_BLOCKS_PER_FRAME = 3;

const SOCKET_BLOCKED =
  "Die Verbindung für Sprachgespräche wurde nicht aufgebaut. Wahrscheinlich " +
  "blockiert das Netzwerk sie. Bitte an die IT weitergeben.";

/**
 * The microphone, with a deadline.
 *
 * `getUserMedia` never rejects on its own while the permission dialog sits
 * unanswered — one of the ways a call used to hang forever. The late-grant path
 * matters just as much: if permission arrives after we gave up, the stream must
 * be released, or the browser keeps recording on a call that no longer exists.
 */
function recorderWithTimeout(
  ms: number,
  message: string,
  onChunk: (samples: Float32Array) => void,
): Promise<Recorder> {
  const request = startRecorder(onChunk);
  let granted = false;
  const deadline = new Promise<never>((_, reject) => {
    setTimeout(() => {
      if (granted) return;
      void request.then((late) => late.stop()).catch(() => undefined);
      reject(new Error(message));
    }, ms);
  });
  return Promise.race([
    request.then((recorder) => {
      granted = true;
      return recorder;
    }),
    deadline,
  ]);
}

/**
 * Where the call belongs. Each variant grounds the conversation differently, so
 * the target is chosen once here instead of being threaded through every caller
 * as a pile of optional fields.
 */
type StartArgs =
  | {
      target?: "session";
      /** The training conversation this voice session belongs to. */
      sessionId: string;
      voice?: string;
      mode?: "roleplay" | "coach";
    }
  | {
      target: "coach-thread";
      /** Created on the first turn, so it may not exist yet when dialling. */
      threadId?: string;
      /** The chat mode the call inherits: open exchange or quizzing. */
      chatMode?: ChatMode;
      trainingPhase?: GespraechsPhase;
      produkt?: string;
    }
  | {
      /** The coach about a whole Durchlauf, called from below the debrief. */
      target: "run";
      runId: string;
    };

type RealtimeEvent =
  | {
      type: "conversation.item.input_audio_transcription.completed";
      transcript: string;
    }
  | {
      type: "conversation.item.input_audio_transcription.delta";
      item_id: string;
      delta: string;
    }
  | {
      type: "response.output_audio.delta";
      delta: string;
    }
  | {
      type: "response.output_audio_transcript.delta";
      response_id: string;
      delta: string;
    }
  | {
      type: "response.output_audio_transcript.done";
      response_id: string;
      transcript: string;
    }
  | { type: "input_audio_buffer.speech_started" }
  | { type: "error"; error: { message?: string } }
  | { type: string };

/**
 * Voice conversation over Socket.IO.
 *
 * Important for the data protection assessment, and changed on 12.08.2026: audio
 * no longer flows browser-to-provider. The customer's network blocks the UDP that
 * WebRTC needs — measured, and confirmed by ChatGPT's and Perplexity's voice modes
 * failing there too — so the audio travels over our server, which now sees it.
 * What that buys: the transcript and the billed duration are written server-side
 * and are no longer a claim of the client.
 *
 * The surface of this hook is unchanged from the WebRTC version on purpose, so
 * the call overlay and its four callers stayed untouched.
 */
export function useVoiceSession() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<VoiceStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [microphoneLabel, setMicrophoneLabel] = useState("");
  const [muted, setMuted] = useState(false);
  const [turns, setTurns] = useState<VoiceTurn[]>([]);
  const [partialUser, setPartialUser] = useState("");
  const [partialAssistant, setPartialAssistant] = useState("");

  const recorderRef = useRef<Recorder | null>(null);
  const playerRef = useRef<PcmPlayer | null>(null);
  const mutedRef = useRef(false);
  const assistantBufRef = useRef<Map<string, string>>(new Map());
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);

  // Synchronous mirrors of the collected transcript, read by `stop()` without
  // depending on React state having flushed. `turnsRef` is the authoritative
  // collection; the `turns` state only drives the UI.
  const turnsRef = useRef<VoiceTurn[]>([]);
  const partialUserRef = useRef("");
  // True between the user starting to speak and that utterance's transcript
  // arriving — tells `stop()` whether it must wait for a pending transcript.
  const awaitingUserRef = useRef(false);
  // Resolves the grace wait early as soon as a pending transcript lands.
  const endWaiterRef = useRef<(() => void) | null>(null);
  const endingRef = useRef(false);

  /**
   * Counts calls in this tab.
   *
   * A `live:closed` or `live:error` of a call that is already over must not tear
   * down the *next* one: hanging up and immediately redialling is a normal thing
   * to do, and the server's closing message can arrive after the new call has
   * started. Every handler checks its generation before touching state.
   */
  const generationRef = useRef(0);
  /** True between `live:start` and the end of the call — what hangUp checks. */
  const activeRef = useRef(false);

  const [stage, setStage] = useState<VoiceStage>("idle");
  // Mirror of `stage` for the async paths below, which must not depend on React
  // state having flushed.
  const stageRef = useRef<VoiceStage>("idle");
  const readyTimerRef = useRef<number | null>(null);

  const goStage = useCallback((next: VoiceStage) => {
    stageRef.current = next;
    setStage(next);
  }, []);

  /** Handlers of the current call, so detaching cannot hit another call's. */
  // `unknown[]` und nicht `never[]`: `socket.off` erwartet einen Listener, der
  // beliebige Argumente annimmt.
  const handlersRef = useRef<
    { event: string; fn: (...args: unknown[]) => void }[]
  >([]);

  const detach = useCallback(() => {
    const socket = getSocket();
    for (const { event, fn } of handlersRef.current) {
      socket.off(event, fn);
    }
    handlersRef.current = [];
  }, []);

  const cleanup = useCallback(() => {
    if (readyTimerRef.current !== null) {
      window.clearTimeout(readyTimerRef.current);
      readyTimerRef.current = null;
    }
    detach();
    recorderRef.current?.stop();
    recorderRef.current = null;
    playerRef.current?.close();
    playerRef.current = null;
    setRemoteStream(null);
    setLocalStream(null);
    setMicrophoneLabel("");
    assistantBufRef.current.clear();
    mutedRef.current = false;
    setMuted(false);
    stageRef.current = "idle";
    setStage("idle");
  }, [detach]);

  /**
   * Ends a call from our side: tells the server, then tidies up locally.
   *
   * Necessary because the socket is a per-tab singleton that survives a route
   * change: local cleanup alone left the provider connection open on the server
   * and the per-tab call lock held, so up to 20 further minutes were billed for a
   * call nobody was in — and the advisor could not dial again.
   */
  const hangUp = useCallback(
    (reason?: string) => {
      if (activeRef.current) {
        activeRef.current = false;
        const socket = getSocket();
        if (socket.connected) socket.emit("live:stop", { reason });
      }
      cleanup();
    },
    [cleanup],
  );

  /**
   * Gives up on a connection that never came up. Silent once the call is live or
   * already ending, so a normal hang-up never shows an error.
   */
  const failConnection = useCallback(
    (message: string) => {
      if (endingRef.current || stageRef.current === "live") return;
      setError(message);
      setStatus("error");
      cleanup();
    },
    [cleanup],
  );

  // Auflegen, nicht nur aufräumen: siehe hangUp.
  useEffect(() => {
    return () => hangUp("Ansicht verlassen");
  }, [hangUp]);

  const handleEvent = useCallback((ev: RealtimeEvent) => {
    switch (ev.type) {
      case "input_audio_buffer.speech_started": {
        awaitingUserRef.current = true;
        // Barge-in: the advisor talks over the answer, so the answer stops. Over
        // WebRTC the provider handled this; here the queued audio is ours.
        playerRef.current?.stop();
        break;
      }
      case "response.output_audio.delta": {
        const e = ev as Extract<RealtimeEvent, { type: "response.output_audio.delta" }>;
        const player = (playerRef.current ??= new PcmPlayer());
        player.push(e.delta);
        // The level meter needs a MediaStream; it exists from the first chunk.
        setRemoteStream((prev) => prev ?? player.stream);
        break;
      }
      case "conversation.item.input_audio_transcription.delta": {
        const e = ev as Extract<
          RealtimeEvent,
          { type: "conversation.item.input_audio_transcription.delta" }
        >;
        partialUserRef.current += e.delta;
        setPartialUser((prev) => prev + e.delta);
        break;
      }
      case "conversation.item.input_audio_transcription.completed": {
        const e = ev as Extract<
          RealtimeEvent,
          { type: "conversation.item.input_audio_transcription.completed" }
        >;
        const text = e.transcript?.trim();
        partialUserRef.current = "";
        awaitingUserRef.current = false;
        setPartialUser("");
        if (text) {
          turnsRef.current = [...turnsRef.current, { role: "user", text }];
          setTurns((prev) => [...prev, { role: "user", text }]);
        }
        endWaiterRef.current?.();
        break;
      }
      case "response.output_audio_transcript.delta": {
        const e = ev as Extract<
          RealtimeEvent,
          { type: "response.output_audio_transcript.delta" }
        >;
        const cur = assistantBufRef.current.get(e.response_id) ?? "";
        assistantBufRef.current.set(e.response_id, cur + e.delta);
        setPartialAssistant(
          Array.from(assistantBufRef.current.values()).join(""),
        );
        break;
      }
      case "response.output_audio_transcript.done": {
        const e = ev as Extract<
          RealtimeEvent,
          { type: "response.output_audio_transcript.done" }
        >;
        const text = e.transcript?.trim();
        assistantBufRef.current.delete(e.response_id);
        setPartialAssistant("");
        if (text) {
          turnsRef.current = [...turnsRef.current, { role: "assistant", text }];
          setTurns((prev) => [...prev, { role: "assistant", text }]);
        }
        endWaiterRef.current?.();
        break;
      }
      case "error": {
        const e = ev as Extract<RealtimeEvent, { type: "error" }>;
        setError(e.error?.message ?? "Unbekannter Realtime-Fehler");
        setStatus("error");
        // Auch abräumen, nicht nur anzeigen: sonst läuft die Aufnahme weiter, das
        // Aufnahmesymbol des Browsers bleibt an und der Server leitet die Blöcke
        // bis zur Zeitgrenze weiter — bezahlte Minuten für ein Gespräch, das
        // sichtbar als Fehler dasteht.
        hangUp("Fehler des Anbieters");
        break;
      }
    }
  }, [hangUp]);

  const start = useCallback(
    async (args: StartArgs) => {
      setError(null);
      setTurns([]);
      setPartialUser("");
      setPartialAssistant("");
      turnsRef.current = [];
      partialUserRef.current = "";
      awaitingUserRef.current = false;
      assistantBufRef.current.clear();
      endingRef.current = false;
      setStatus("connecting");

      try {
        // Microphone first: it is the only step that waits for a human, and
        // asking for it after the session was opened would keep a paid
        // conversation waiting on a permission dialog.
        goStage("mic");
        const socket = getSocket();
        const pending: Float32Array[] = [];
        const recorder = await recorderWithTimeout(
          MIC_TIMEOUT_MS,
          "Kein Mikrofonzugriff. Bitte die Berechtigung im Browser erteilen " +
            "und erneut anrufen.",
          (samples) => {
            if (mutedRef.current) return;
            pending.push(samples);
            if (pending.length < AUDIO_BLOCKS_PER_FRAME) return;
            // Joined before encoding: base64 strings cannot be concatenated.
            const joined = concatSamples(pending.splice(0));
            socket.emit("live:audio", { audio: floatToPcm16Base64(joined) });
          },
        );
        recorderRef.current = recorder;
        setLocalStream(recorder.stream);
        setMicrophoneLabel(recorder.microphoneLabel);

        goStage("socket");
        if (!(await socketReady(SOCKET_TIMEOUT_MS))) {
          throw new Error(SOCKET_BLOCKED);
        }

        goStage("session");
        const generation = ++generationRef.current;
        const mine = () => generationRef.current === generation;

        const listen = <T,>(event: string, fn: (payload: T) => void) => {
          const guarded = (payload: T) => {
            if (mine()) fn(payload);
          };
          handlersRef.current.push({
            event,
            fn: guarded as (...args: unknown[]) => void,
          });
          socket.on(event, guarded);
        };

        listen<RealtimeEvent>("live:event", (event) => handleEvent(event));
        listen<unknown>("live:worksheet-updated", () => {
          // A sidecar has inspected a completed customer reply. Reload the run
          // now so the worksheet fills during the call instead of waiting for
          // the periodic fallback refresh or for hang-up.
          void queryClient.invalidateQueries({
            predicate: (query) => {
              const head = query.queryKey[0] as { _id?: unknown } | undefined;
              return head?._id === "processRunControllerGet";
            },
          });
        });
        listen<{ message?: string }>("live:error", (payload) => {
          activeRef.current = false;
          setError(payload?.message ?? "Die Sprachverbindung ist abgebrochen.");
          setStatus("error");
          cleanup();
        });
        listen<{ reason?: string }>("live:closed", (payload) => {
          activeRef.current = false;
          if (endingRef.current) return;

          // The server hung up on its own (time limit, upstream gone). Anything
          // collected is already persisted there, so only the UI is left.
          //
          // Deliberately *not* back to "idle": the overlay dials whenever it is
          // open and the status is idle, so that put the advisor straight into a
          // brand-new 20-minute session, billed, right after the message that the
          // last one had ended. An ended call is a state to look at, not a
          // starting gun.
          setError(
            payload?.reason === "Zeitgrenze"
              ? "Das Gespräch wurde nach 20 Minuten beendet."
              : "Das Gespräch wurde beendet.",
          );
          setStatus("error");
          cleanup();
        });
        listen<unknown>("live:ready", () => {
          if (readyTimerRef.current !== null) {
            window.clearTimeout(readyTimerRef.current);
            readyTimerRef.current = null;
          }
          goStage("live");
          setStatus("live");
        });

        activeRef.current = true;
        socket.emit("live:start", { body: toStartBody(args) });

        // From here the provider decides, and it is not awaitable: either the
        // session comes up or nothing happens at all. Without this timer that
        // silence was the endless "Verbinde…".
        readyTimerRef.current = window.setTimeout(
          () => failConnection("Der Dienst antwortet nicht. Bitte erneut versuchen."),
          SESSION_TIMEOUT_MS,
        );
      } catch (e) {
        const err = e as Error;
        setError(err?.message ?? "Unbekannter Fehler");
        setStatus("error");
        cleanup();
      }
    },
    [cleanup, failConnection, goStage, handleEvent, queryClient],
  );

  const stop = useCallback(async (): Promise<VoiceTurn[]> => {
    if (endingRef.current) return turnsRef.current;
    endingRef.current = true;
    setStatus("ending");

    // Stop capturing input right away, but stay connected for a short grace
    // period *if* a user utterance is still being transcribed: the transcript
    // only arrives *after* speech ends, so it would otherwise be lost on
    // hang-up. The wait resolves early the moment that transcript lands, and is
    // capped so ending never feels stuck.
    mutedRef.current = true;
    if (awaitingUserRef.current || partialUserRef.current.trim().length > 0) {
      await new Promise<void>((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          endWaiterRef.current = null;
          resolve();
        };
        endWaiterRef.current = finish;
        setTimeout(finish, 2500);
      });
    }

    // What the UI shows. The authoritative copy is the server's: it collected the
    // same turns from the same events and has already written them.
    const collected = [...turnsRef.current];
    const pendingUser = partialUserRef.current.trim();
    if (pendingUser) collected.push({ role: "user", text: pendingUser });
    const pendingAssistant = Array.from(assistantBufRef.current.values())
      .join("")
      .trim();
    if (pendingAssistant) {
      collected.push({ role: "assistant", text: pendingAssistant });
    }

    // Tells the server to close and persist. No transcript travels with it —
    // that was the old arrangement, and it is why two calls once ended up with
    // no trace at all.
    //
    // Awaited, because the callers reload the thread the moment this resolves: a
    // fire-and-forget hang-up made them race the write and show a conversation
    // without its call. Capped, so a lost connection cannot make ending hang.
    const socket = getSocket();
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        socket.off("live:closed", finish);
        resolve();
      };
      socket.once("live:closed", finish);
      window.setTimeout(finish, 3_000);
      // Der reguläre Weg zum Auflegen; hangUp deckt die unregulären ab.
      activeRef.current = false;
      socket.emit("live:stop", {});
    });

    cleanup();
    setStatus("idle");
    return collected;
  }, [cleanup]);

  const switchMicrophone = useCallback(async (deviceId: string | null) => {
    const recorder = recorderRef.current;
    if (!recorder || endingRef.current) throw new Error("No active recorder");
    await recorder.switchMicrophone(deviceId);
    if (recorderRef.current === recorder) setMicrophoneLabel(recorder.microphoneLabel);
  }, []);

  const toggleMute = useCallback(() => {
    mutedRef.current = !mutedRef.current;
    setMuted(mutedRef.current);
  }, []);

  return {
    status,
    stage,
    error,
    muted,
    turns,
    partialUser,
    partialAssistant,
    remoteStream,
    localStream,
    start,
    stop,
    toggleMute,
    switchMicrophone,
    microphoneLabel,
  };
}

/** The wire shape the live lane expects, derived from the caller's target. */
function toStartBody(args: StartArgs): Record<string, unknown> {
  switch (args.target) {
    case "coach-thread":
      return {
        target: "coach-thread",
        threadId: args.threadId,
        chatMode: args.chatMode,
        trainingPhase: args.trainingPhase,
        produkt: args.produkt,
      };
    case "run":
      return { target: "run", runId: args.runId };
    default:
      return {
        target: "session",
        sessionId: args.sessionId,
        mode: args.mode ?? "roleplay",
        voice: args.voice,
      };
  }
}
